import { Component, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDatepicker } from "@angular/material/datepicker";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { FormControl } from "@angular/forms";
import { DatePipe } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { PlanUpgradeDialogComponent } from "src/app/components/plan-upgrade-dialog/plan-upgrade-dialog.component";
import { Subscription, combineLatest, forkJoin, of } from "rxjs";
import moment from "moment";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { debounceTime, catchError, map } from "rxjs/operators";
import { DateTime } from "luxon";
import { ProjectService } from "src/app/Services/project.service";
import { ScreenshotTimeEnrichment, ScreenshotTimeService, ScreenshotTimeView } from "src/app/Services/screenshot-time.service";
import { SharedService } from "src/app/Services/sharedService";
import { TokenEncryptionService } from "src/app/Services/token-encryption.service";
import { ShiftService } from "src/app/Services/shift.service";
import { Shift } from "src/app/models/shift.model";
import { ScreenshotFancyboxService } from "src/app/Services/screenshot-fancybox.service";
import { EntitlementsService } from "src/app/Services/entitlements.service";
import { MessagingServiceService } from "src/app/Services/messaging-service.service";
import { isRustAgentUser } from "src/app/agent-deploy/agent-deploy.config";
import { sysConfig } from "src/app/sysConfig";
import { ConductTimelog, ConductTimelogGroup } from "src/app/interface/conduct-timelog";

type BucketSizeMinutes = 15 | 30 | 60 | 120 | 240;

const DAYPART_LABELS = ["Late Night", "Morning", "Afternoon", "Evening", "Night"] as const;
type DaypartLabel = (typeof DAYPART_LABELS)[number];

type MatchConfidence = "exact_match" | "near_match" | "no_match";

type ScreenshotItem = any & {
  _parsedUtcMs?: number | null;
  _bucketKey?: string;
  _windowName?: string | null;
  _matchConfidence?: MatchConfidence;
  _imgError?: boolean;
  _appIconErr?: boolean;
  _conductProject?: string | null;
  _conductTask?: string | null;
  _isConductTracked?: boolean;
  timeDisplay?: any;
  timeagoInput?: Date | null;
  showAbsoluteTime?: boolean;
};

/** WP screenshots API context for Work Replay entitlement checks (SUSEE-1344). */
const WORK_REPLAY_SCREENSHOT_CONTEXT = "work_replay";

export interface ConductBucketTask {
  projectName: string;
  taskTitle: string;
  durationSeconds: number;
  totalDurationSeconds?: number;
  workedHours: string;
  totalWorkedHours?: string;
  timeRange?: string;
  fromTime?: string;
  endTime?: string;
}

export interface HoveredConductTooltipData {
  projectName: string;
  slotLoggedTime: string;
  tasksCount: number;
  tasks: Array<{
    taskTitle: string;
    timeRange?: string;
    workedHours: string;
    totalWorkedHours?: string;
  }>;
  moreCount: number;
  usageComparison: string | null;
  x: number;
  y: number;
  caretOffset: number;
  placement: 'top' | 'bottom';
}

export interface AvailableProject {
  name: string;
  totalHours: string;
  totalSeconds: number;
}

type TimelineBucket = {
  key: string;
  startUtcMs: number;
  endUtcMs: number;
  label: string;
  screenshots: ScreenshotItem[];
  usageSeconds: number;
  idleSeconds: number;
  state: "active" | "idle" | "mixed" | "active_no_screenshot" | "no_data";
  conductTasks?: ConductBucketTask[];
  conductTotalSeconds?: number;
};

@Component({
  selector: "app-work-replay",
  templateUrl: "./work-replay.component.html",
  styleUrls: ["./work-replay.component.css"],
  providers: [DatePipe],
  standalone: false,
})
export class WorkReplayComponent implements OnInit, OnDestroy {
  @ViewChild("picker") private datePicker?: MatDatepicker<Date>;

  bucketSize: BucketSizeMinutes = 60;
  filtersCollapsed = false;

  projectFilterCtrl = new FormControl<string>("all");
  conductActivityFilterCtrl = new FormControl<string>("all");
  appFilterCtrl = new FormControl<string>("all");
  typeFilterCtrl = new FormControl<string>("all");
  /** View filter: selected day-part sections; empty = all times. */
  daypartFilterCtrl = new FormControl<string[]>([]);
  readonly daypartFilterOptions = DAYPART_LABELS;
  /** Full-day timeline (00:00–23:59 in company shift TZ) includes empty buckets by default. */
  showEmptySlotsCtrl = new FormControl<boolean>(true);
  /** Toggle to include Conduct project timelogs across timeline and unlock Conduct project filter. */
  showConductLogsCtrl = new FormControl<boolean>(true);
  employees: any[] = [];

  conductTimelogs: any[] = [];
  availableProjects: AvailableProject[] = [];
  hoveredConductTooltipData: HoveredConductTooltipData | null = null;
  loadingConduct = false;

  isConductIntegrated = false;
  isConductDrawerOpen = false;
  selectedConductGroup: ConductTimelogGroup | null = null;
  conductDrawerGroups: ConductTimelogGroup[] = [];
  selectedConductGroupIndex = 0;

  selectedDate: Date | null = null;
  minDate: any;
  maxDate: any;
  serverDate: any = new Date();

  userId: string | null = null;
  userDetails: any = null;

  loadingEmployees = false;
  /** Shown when the employee API fails or returns an unexpected shape. */
  employeesLoadError: string | null = null;
  loadingUser = false;
  loadingScreenshots = false;
  loadingLogs = false;

  shiftTimezone: string | null = null;
  shiftInfo: Shift | null = null;
  regionHint: string | null = null;
  /** True after company shift config is resolved (success or failure). Gates data fetch. */
  private shiftContextLoaded = false;
  /** Cached active company shifts; used to resolve per-employee assignment without extra API calls. */
  private companyShifts: Shift[] = [];
  /** Guards async employee-detail callbacks from applying a stale employee's shift. */
  private employeeDetailsRequestId = 0;
  private subs = new Subscription();

  screenshots: ScreenshotItem[] = [];
  filteredScreenshots: ScreenshotItem[] = [];
  screenshotsTotal = 0;
  screenshotsLoaded = 0;
  availableApps: string[] = [];

  logsByDate: any = null;
  flattenedLogSessions: Array<{
    inUtcMs: number;
    outUtcMs: number;
    appName?: string;
    windowName?: string;
    usageSeconds?: number;
    idleSeconds?: number;
  }> = [];

  buckets: TimelineBucket[] = [];
  bucketGroups: Array<{ label: string; buckets: TimelineBucket[]; bucketCount: number; screenshotCount: number }> = [];
  expandedBucketKeys = new Set<string>();
  collapsedDayparts = new Set<string>();
  private pendingFocusTime: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private datePipe: DatePipe,
    private projectService: ProjectService,
    private screenshotTime: ScreenshotTimeService,
    private sharedService: SharedService,
    private tokenEncryptionService: TokenEncryptionService,
    private shiftService: ShiftService,
    private screenshotFancybox: ScreenshotFancyboxService,
    private entitlementsService: EntitlementsService,
    private dialog: MatDialog,
    private messagingService: MessagingServiceService,
  ) { }

  ngOnInit(): void {
    this.entitlementsService.syncFromSession();
    this.initServerDateRange();
    this.initShiftContext();
    this.initConductContext();
    // Snapshot first so child employee-search gets userId on the initial render (refresh / deep link).
    this.applyRouteState(this.route.snapshot.params, this.route.snapshot.queryParams);
    this.initRouteState();
    this.initReplayFilters();
    this.loadEmployees();
  }

  ngOnDestroy(): void {
    this.hoveredConductTooltipData = null;
    this.pendingFocusTime = null;
    if (this.isConductDrawerOpen) {
      this.closeConductDrawer();
    }
    this.subs.unsubscribe();
  }

  get isLanding(): boolean {
    return !this.userId;
  }

  /** Conduct Activity filter closed label */
  get conductActivityFilterTriggerLabel(): string {
    const raw = this.conductActivityFilterCtrl.value;
    const v = raw == null || raw === "" ? "all" : String(raw).toLowerCase();
    if (v === "tracked") return "Tracked activity";
    if (v === "untracked") return "Untracked activity";
    return "All activity";
  }

  /**
   * Material Symbols name for Activity filter.
   */
  matIconForActivityFilter(value: string | null | undefined): string {
    const v = String(value ?? "all").toLowerCase();
    if (v === "tracked") return "task_alt";
    if (v === "untracked") return "pending_actions";
    return "browse_activity";
  }

  get untrackedSlotsCount(): number {
    return this.buckets.filter((b) => (b.conductTasks?.length || 0) > 0 && !b.screenshots?.length).length;
  }

  /** Conduct Project filter closed label */
  get projectFilterTriggerLabel(): string {
    const raw = this.projectFilterCtrl.value;
    const v = raw == null || raw === "" ? "all" : String(raw);
    if (v.toLowerCase() === "all") {
      return "All projects";
    }
    const found = this.availableProjects.find((p) => p.name.toLowerCase() === v.toLowerCase());
    return found ? `${found.name} (${found.totalHours})` : v;
  }

  /** App filter closed label + glyph source (drives `appIconGlyph` pipe). */
  get appFilterTriggerLabel(): string {
    const raw = this.appFilterCtrl.value;
    const v = raw == null || raw === "" ? "all" : String(raw).toLowerCase();
    if (v === "all") {
      return "All apps";
    }
    const found = this.availableApps.find((a) => (a || "").toLowerCase() === v);
    return found || String(raw);
  }

  /** Selected day-parts; empty or all five = no narrowing. */
  get selectedDayparts(): string[] {
    return (this.daypartFilterCtrl.value || []).filter(Boolean);
  }

  get isDaypartFilterShowingAll(): boolean {
    const n = this.selectedDayparts.length;
    return n === 0 || n >= DAYPART_LABELS.length;
  }

  /** Time-of-day filter closed label. */
  get daypartFilterTriggerLabel(): string {
    const selected = this.selectedDayparts;
    if (this.isDaypartFilterShowingAll) {
      return "All times";
    }
    if (selected.length === 1) {
      return selected[0];
    }
    if (selected.length === 2) {
      return `${selected[0]}, ${selected[1]}`;
    }
    return `${selected[0]} + ${selected.length - 1} more`;
  }

  /** Icon for closed multi-select trigger. */
  get daypartFilterTriggerIcon(): string {
    const selected = this.selectedDayparts;
    if (this.isDaypartFilterShowingAll) {
      return "all";
    }
    if (selected.length === 1) {
      return selected[0];
    }
    return "all";
  }

  /** Bucket groups shown in the timeline (respects time-of-day filter). */
  get visibleBucketGroups(): Array<{
    label: string;
    buckets: TimelineBucket[];
    bucketCount: number;
    screenshotCount: number;
  }> {
    if (this.isDaypartFilterShowingAll) {
      return this.bucketGroups;
    }
    const allowed = new Set(this.selectedDayparts);
    return this.bucketGroups.filter((g) => allowed.has(g.label));
  }

  /** Type filter closed label (matches mat-option copy). */
  get typeFilterTriggerLabel(): string {
    const v = (this.typeFilterCtrl.value || "all").toLowerCase();
    if (v === "all") return "All types";
    return this.mapTypeLabel(v);
  }

  /**
   * Material Symbols name for Type filter (same glyphs as `app-ss-card` type chip).
   */
  matIconForTypeFilter(value: string | null | undefined): string {
    const v = String(value ?? "all").toLowerCase();
    if (v === "all") return "category";
    if (v === "periodic") return "schedule";
    if (v === "app-changed") return "swap_horiz";
    if (v === "admin-request") return "photo_camera";
    return "category";
  }

  /** Closed bucket-size label (matches mat-option copy). */
  get bucketSizeTriggerLabel(): string {
    return this.formatBucketSizeLabel(this.bucketSize);
  }

  formatBucketSizeLabel(m: number | BucketSizeMinutes): string {
    switch (Number(m) as BucketSizeMinutes) {
      case 15:
        return "15 min";
      case 30:
        return "30 min";
      case 60:
        return "1 hour";
      case 120:
        return "2 hours";
      case 240:
        return "4 hours";
      default:
        return "1 hour";
    }
  }

  /**
   * Material Symbols for bucket size: each step is a visibly distinct glyph
   * (fine slices → stopwatch → wall clock → single-day sheet → month grid).
   */
  matIconForBucketSize(m: number | null | undefined): string {
    switch (Number(m)) {
      case 15:
        return "more_time";
      case 30:
        return "timer";
      case 60:
        return "schedule";
      case 120:
        return "calendar_today";
      case 240:
        return "calendar_month";
      default:
        return "schedule";
    }
  }

  private initServerDateRange(): void {
    this.subs.add(
      this.projectService.getUser().subscribe((data: any) => {
        if (data?.meta?.today_date) {
          const parsedDate = new Date(data.meta.today_date);
          this.serverDate = isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
        } else {
          this.serverDate = new Date();
        }

        if (this.isWorkReplayPreviewOnly()) {
          const yesterday = this.getYesterdayDate(this.serverDate);
          this.minDate = yesterday;
          this.maxDate = yesterday;
          this.selectedDate = this.clampToAllowedWorkReplayDate(
            this.selectedDate ?? yesterday
          );
        } else {
          const companyId =
            this.tokenEncryptionService.getEncryptedItem<any>("logedInUser")?.company_id ??
            data?.company_id;

          this.subs.add(
          this.sharedService
            .getValidationDateRangeByCompany(this.serverDate, companyId, {
              lookbackDays: this.entitlementsService.getScreenshotsRetentionDays(),
            })
            .subscribe((range) => {
              this.minDate = range.minDate;
              this.maxDate = range.maxDate;

              if (this.selectedDate != null) {
                this.selectedDate = this.clampToAllowedWorkReplayDate(this.selectedDate);
                return;
              }

              // Default: yesterday in company shift timezone (same as dashboard date semantics).
              this.subs.add(
                this.sharedService
                  .getValidationDateRangeByCompany(this.serverDate, companyId, { maxDayOffset: -1 })
                  .subscribe((yesterdayRange) => {
                    this.selectedDate = this.toDateOnly(yesterdayRange.maxDate);
                    if (this.shiftContextLoaded && this.userId) {
                      this.loadWorkReplayData();
                    }
                  })
              );
            })
          );
        }
      })
    );
  }

  private toDateOnly(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  isWorkReplayPreviewOnly(): boolean {
    return this.entitlementsService.isWorkReplayPreviewOnly();
  }

  /** Basic preview: date navigation uses upgrade modal (same pattern as Live Monitoring). */
  isWorkReplayDateNavigationLocked(): boolean {
    return this.isWorkReplayPreviewOnly();
  }

  onDatepickerOpened(): void {
    if (!this.isWorkReplayDateNavigationLocked()) {
      return;
    }
    queueMicrotask(() => this.datePicker?.close());
    this.promptWorkReplayDateUpgrade();
  }

  private promptWorkReplayDateUpgrade(): void {
    this.promptWorkReplayUpgradeDialog({
      title: "Upgrade for full history",
      message:
        "Date navigation is included with full Work Replay on Professional and above. Basic preview is limited to yesterday.",
    });
  }

  private promptWorkReplayUpgradeDialog(options: {
    title: string;
    message: string;
    confirmLabel?: string;
    navigateOnConfirm?: boolean;
  }): void {
    const ref = this.dialog.open(PlanUpgradeDialogComponent, {
      width: "420px",
      maxWidth: "94vw",
      data: {
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel,
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed && options.navigateOnConfirm !== false) {
        void this.router.navigate(["/billings"], { queryParams: { intent: "upgrade" } });
      }
    });
  }

  private getYesterdayDate(serverDate: Date): Date {
    const d = new Date(serverDate);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 1);
    return d;
  }

  private clampToAllowedWorkReplayDate(date: Date): Date {
    if (!this.isWorkReplayPreviewOnly()) {
      return date;
    }
    const allowed =
      this.minDate != null
        ? this.toDateOnly(new Date(this.minDate))
        : this.getYesterdayDate(this.serverDate);
    return new Date(allowed.getTime());
  }

  private initShiftContext(): void {
    const companyId = this.tokenEncryptionService.getEncryptedItem<any>("logedInUser")?.company_id;
    if (!companyId) {
      this.companyShifts = [];
      this.applyResolvedShift();
      this.markShiftContextReady();
      return;
    }

    this.subs.add(
      this.shiftService.getShifts(Number(companyId)).subscribe({
        next: (shifts) => {
          this.subs.add(
            this.hydrateShiftAssignments(Array.isArray(shifts) ? shifts : []).subscribe({
              next: (hydrated) => {
                this.companyShifts = hydrated;
                this.applyResolvedShift();
                this.markShiftContextReady();
              },
              error: () => {
                this.companyShifts = Array.isArray(shifts) ? shifts : [];
                this.applyResolvedShift();
                this.markShiftContextReady();
              },
            })
          );
        },
        error: () => {
          this.companyShifts = [];
          this.shiftInfo = null;
          this.shiftTimezone = null;
          this.regionHint = null;
          this.markShiftContextReady();
        },
      })
    );
  }

  private initConductContext(): void {
    const user = this.tokenEncryptionService.getEncryptedItem<any>("user");
    const loggedInUser = this.tokenEncryptionService.getEncryptedItem<any>("logedInUser");
    const companyId = Number(loggedInUser?.company_id ?? user?.company_id ?? user?.id ?? 0);
    if (!companyId) return;

    this.subs.add(
      this.projectService.getConductIntegrationStatus(companyId).subscribe({
        next: (statusRes: any) => {
          this.isConductIntegrated = !!(statusRes?.is_integrated || statusRes?.status === "active" || statusRes?.connected);
        },
        error: () => {
          this.isConductIntegrated = false;
        }
      })
    );
  }

  /**
   * Populate employee_ids from GET /shifts/{id}/employees when the list payload omits them.
   * Shift Management uses this endpoint as the assignment source of truth.
   */
  private hydrateShiftAssignments(shifts: Shift[]) {
    const needsHydration = shifts.filter(
      (s) => Number(s?.id) > 0 && (!Array.isArray(s.employee_ids) || s.employee_ids.length === 0)
    );
    if (!needsHydration.length) {
      return of(shifts);
    }

    return forkJoin(
      needsHydration.map((s) =>
        this.shiftService.getShiftEmployees(Number(s.id)).pipe(
          map((employees) => ({
            shiftId: Number(s.id),
            ids: (employees || [])
              .filter((e) => e?.on_this_shift)
              .map((e) => Number(e.user_id))
              .filter((id) => Number.isFinite(id)),
          })),
          catchError(() => of({ shiftId: Number(s.id), ids: [] as number[] }))
        )
      )
    ).pipe(
      map((rows) => {
        const byId = new Map(rows.map((r) => [r.shiftId, r.ids]));
        return shifts.map((s) => {
          const ids = byId.get(Number(s.id));
          if (!ids) {
            return s;
          }
          return {
            ...s,
            employee_ids: ids,
            employee_count: ids.length || s.employee_count || 0,
          };
        });
      })
    );
  }

  /**
   * Resolve header from Shift Management assignment only (employee_ids).
   * Do not use user-meta shift ids — they often hold the company default and overwrite the real assignment.
   * Unassigned employees keep shiftInfo null (UI shows "Shift not assigned").
   * Day-bound timezone still falls back to company first-shift TZ so timeline math stays stable.
   */
  private applyResolvedShift(): void {
    const resolved = this.userId
      ? this.shiftService.resolveEmployeeShift(this.companyShifts, this.userId)
      : null;
    this.shiftInfo = resolved;
    const tzSource = resolved?.timezone ?? this.companyShifts[0]?.timezone ?? null;
    this.shiftTimezone = tzSource && this.screenshotTime.isValidIanaZone(tzSource) ? tzSource : null;
    this.regionHint = this.getWorkReplayRegionHint();
  }

  /** True after company shifts load attempt finished (success or failure). */
  get shiftContextReady(): boolean {
    return this.shiftContextLoaded;
  }

  /** Work Replay shows office (shift) time first; hint copy matches that behavior. */
  private getWorkReplayRegionHint(): string | null {
    if (!this.shiftTimezone || !this.screenshotTime.isValidIanaZone(this.shiftTimezone)) {
      return null;
    }
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (viewerZone === this.shiftTimezone) {
      return null;
    }
    try {
      const now = DateTime.now();
      const za = now.setZone(viewerZone);
      const zb = now.setZone(this.shiftTimezone);
      if (za.isValid && zb.isValid && za.offset === zb.offset) {
        return null;
      }
    } catch {
      /* fall through */
    }
    const short = this.shiftTimezone.split("/").pop()?.replace(/_/g, " ") ?? this.shiftTimezone;
    return `Full day (12:00 AM–11:59 PM) uses your company timezone (${short}). Times are shown in that zone, not UTC.`;
  }

  private markShiftContextReady(): void {
    if (this.shiftContextLoaded) {
      return;
    }
    this.shiftContextLoaded = true;
    if (this.userId && this.selectedDate) {
      this.loadWorkReplayData();
    }
  }

  private initRouteState(): void {
    this.subs.add(
      combineLatest([this.route.params, this.route.queryParams]).subscribe(
        ([params, queryParams]: [Params, Params]) => {
          this.applyRouteState(params, queryParams);
        }
      )
    );
  }

  private applyRouteState(params: Params, queryParams: Params): void {
    const nextUserId = queryParams?.employeeId ? String(queryParams.employeeId) : null;
    const userChanged = this.userId !== nextUserId;
    this.userId = nextUserId;

    const qpDate = typeof queryParams?.date === "string" ? queryParams.date : null;
    let dateChanged = false;
    if (qpDate) {
      const parsed = new Date(qpDate);
      if (!isNaN(parsed.getTime())) {
        const clamped = this.clampToAllowedWorkReplayDate(parsed);
        dateChanged = this.selectedDate?.getTime() !== clamped.getTime();
        this.selectedDate = clamped;
      }
    }

    if (dateChanged) {
      this.conductTimelogs = [];
      this.availableProjects = [];
      this.hoveredConductTooltipData = null;
    }

    let filterChanged = false;
    let requiresRebuild = false;

    if (queryParams['bucketSize']) {
      const bs = Number(queryParams['bucketSize']) as BucketSizeMinutes;
      if (this.bucketSize !== bs) {
        this.bucketSize = bs;
        filterChanged = true;
        requiresRebuild = true;
      }
    }

    const projectVal = queryParams['project'] ? String(queryParams['project']).toLowerCase() : 'all';
    if (this.projectFilterCtrl.value !== projectVal) {
      this.projectFilterCtrl.setValue(projectVal, { emitEvent: false });
      filterChanged = true;
    }

    const activityVal = queryParams['conductActivity'] ? String(queryParams['conductActivity']).toLowerCase() : 'all';
    if (this.conductActivityFilterCtrl.value !== activityVal) {
      this.conductActivityFilterCtrl.setValue(activityVal, { emitEvent: false });
      filterChanged = true;
    }

    const appVal = queryParams['app'] || 'all';
    if (this.appFilterCtrl.value !== appVal) {
      this.appFilterCtrl.setValue(appVal, { emitEvent: false });
      filterChanged = true;
    }

    const typeVal = queryParams['type'] || 'all';
    if (this.typeFilterCtrl.value !== typeVal) {
      this.typeFilterCtrl.setValue(typeVal, { emitEvent: false });
      filterChanged = true;
    }

    const daypartsStr = queryParams['dayparts'];
    const newDayparts = daypartsStr ? daypartsStr.split(',') : [];
    if (this.daypartFilterCtrl.value?.join(',') !== newDayparts.join(',')) {
      this.daypartFilterCtrl.setValue(newDayparts, { emitEvent: false });
      filterChanged = true;
    }

    const showEmptyStr = queryParams['showEmpty'];
    const showEmpty = showEmptyStr ? showEmptyStr === 'true' : true;
    if (this.showEmptySlotsCtrl.value !== showEmpty) {
      this.showEmptySlotsCtrl.setValue(showEmpty, { emitEvent: false });
      filterChanged = true;
      requiresRebuild = true;
    }

    const showConductStr = queryParams['showConduct'];
    const showConduct = showConductStr ? showConductStr === 'true' : true;
    if (this.showConductLogsCtrl.value !== showConduct) {
      this.showConductLogsCtrl.setValue(showConduct, { emitEvent: false });
      filterChanged = true;
      requiresRebuild = true;
    }

    if (queryParams['time']) {
      this.pendingFocusTime = String(queryParams['time']).trim();
    }

    if (this.userId) {
      if (userChanged) {
        // Clear previous employee details so preferred-shift meta cannot leak across users.
        this.userDetails = null;
        this.conductTimelogs = [];
        this.availableProjects = [];
        this.hoveredConductTooltipData = null;
        this.applyResolvedShift();
        this.loadEmployeeDetails(this.userId);
      }
      if ((userChanged || dateChanged) && this.shiftContextLoaded) {
        this.loadWorkReplayData();
      } else if (filterChanged && !userChanged && !dateChanged) {
        this.applyReplayFilters();
        if (requiresRebuild) {
          this.rebuildBuckets();
        }
      }
    } else {
      this.employeeDetailsRequestId++;
      this.userDetails = null;
      this.conductTimelogs = [];
      this.availableProjects = [];
      this.hoveredConductTooltipData = null;
      this.projectFilterCtrl.setValue('all', { emitEvent: false });
      this.conductActivityFilterCtrl.setValue('all', { emitEvent: false });
      this.showConductLogsCtrl.setValue(true, { emitEvent: false });
      this.applyResolvedShift();
      this.screenshots = [];
      this.filteredScreenshots = [];
      this.buckets = [];
      this.flattenedLogSessions = [];
    }
  }

  private updateQueryParams(): void {
    const queryParams: Params = {};

    if (this.bucketSize !== 60) {
      queryParams['bucketSize'] = this.bucketSize;
    } else {
      queryParams['bucketSize'] = null;
    }

    if (this.conductActivityFilterCtrl.value && this.conductActivityFilterCtrl.value !== 'all') {
      queryParams['conductActivity'] = this.conductActivityFilterCtrl.value;
    } else {
      queryParams['conductActivity'] = null;
    }

    if (this.projectFilterCtrl.value && this.projectFilterCtrl.value !== 'all') {
      queryParams['project'] = this.projectFilterCtrl.value;
    } else {
      queryParams['project'] = null;
    }

    if (this.appFilterCtrl.value && this.appFilterCtrl.value !== 'all') {
      queryParams['app'] = this.appFilterCtrl.value;
    } else {
      queryParams['app'] = null;
    }

    if (this.typeFilterCtrl.value && this.typeFilterCtrl.value !== 'all') {
      queryParams['type'] = this.typeFilterCtrl.value;
    } else {
      queryParams['type'] = null;
    }

    const dayparts = this.daypartFilterCtrl.value;
    if (dayparts && dayparts.length > 0) {
      queryParams['dayparts'] = dayparts.join(',');
    } else {
      queryParams['dayparts'] = null;
    }

    if (this.showEmptySlotsCtrl.value !== true) {
      queryParams['showEmpty'] = this.showEmptySlotsCtrl.value ? 'true' : 'false';
    } else {
      queryParams['showEmpty'] = null;
    }

    if (this.showConductLogsCtrl.value !== true) {
      queryParams['showConduct'] = this.showConductLogsCtrl.value ? 'true' : 'false';
    } else {
      queryParams['showConduct'] = null;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  private initReplayFilters(): void {
    this.subs.add(
      this.projectFilterCtrl.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.updateQueryParams();
        this.applyReplayFilters();
      })
    );
    this.subs.add(
      this.conductActivityFilterCtrl.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.updateQueryParams();
        this.applyReplayFilters();
      })
    );
    this.subs.add(
      this.appFilterCtrl.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.updateQueryParams();
        this.applyReplayFilters();
      })
    );
    this.subs.add(
      this.typeFilterCtrl.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.updateQueryParams();
        this.applyReplayFilters();
      })
    );
    this.subs.add(
      this.daypartFilterCtrl.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.updateQueryParams();
      })
    );
    this.subs.add(
      this.showEmptySlotsCtrl.valueChanges.pipe(debounceTime(50)).subscribe(() => {
        this.updateQueryParams();
        this.rebuildBuckets();
      })
    );
    this.subs.add(
      this.showConductLogsCtrl.valueChanges.pipe(debounceTime(50)).subscribe((val) => {
        if (!val) {
          this.projectFilterCtrl.setValue('all', { emitEvent: false });
          this.conductActivityFilterCtrl.setValue('all', { emitEvent: false });
        }
        this.updateQueryParams();
        this.applyReplayFilters();
      })
    );
  }

  private loadEmployees(): void {
    this.loadingEmployees = true;
    this.employeesLoadError = null;
    // Force refresh so we never reuse a failed/stale shareReplay cache from ProjectService.
    this.subs.add(
      this.projectService.getUserList(0, 0, true).subscribe({
        next: (res: any) => {
          const list = this.normalizeEmployeesFromApi(res);
          this.employees = list;
          this.loadingEmployees = false;
          const unrecognizedShape =
            res != null &&
            typeof res === "object" &&
            !Array.isArray(res) &&
            !Array.isArray(res?.data) &&
            !Array.isArray(res?.users);
          if (list.length === 0 && unrecognizedShape) {
            this.employeesLoadError =
              "Employee list was empty or in an unexpected format. Try Retry, or refresh the page.";
          }
        },
        error: () => {
          this.employees = [];
          this.loadingEmployees = false;
          this.employeesLoadError =
            "Could not load employees. Check your connection and try again.";
        },
      })
    );
  }

  /** Normalize getUserList payloads (array, or { data } / { users }) like other admin screens. */
  private normalizeEmployeesFromApi(res: any): any[] {
    if (Array.isArray(res)) {
      return res.filter((u) => u != null);
    }
    if (res && Array.isArray(res.data)) {
      return res.data.filter((u) => u != null);
    }
    if (res && Array.isArray(res.users)) {
      return res.users.filter((u) => u != null);
    }
    return [];
  }

  retryLoadEmployees(): void {
    this.loadEmployees();
  }

  onEmployeeSelected(userId: string | number | null): void {
    const empty = userId === "" || userId === null || userId === undefined;
    const uid = empty ? null : String(userId);

    if (this.userId === uid) {
      return;
    }

    const dateStr = this.selectedDate
      ? this.datePipe.transform(this.selectedDate, "yyyy-MM-dd")
      : null;

    this.showConductLogsCtrl.setValue(true, { emitEvent: false });
    this.conductActivityFilterCtrl.setValue('all', { emitEvent: false });
    this.projectFilterCtrl.setValue('all', { emitEvent: false });
    this.conductTimelogs = [];
    this.availableProjects = [];
    this.hoveredConductTooltipData = null;

    const queryParams: Params = {
      employeeId: uid,
      ...(dateStr ? { date: dateStr } : {}),
      showConduct: null,
      conductActivity: null,
      project: null,
      app: null,
    };

    this.router.navigate(["/work-replay"], {
      queryParams,
      queryParamsHandling: "merge",
    });
  }

  onDateChanged(date: Date | null): void {
    if (!date) {
      return;
    }
    if (this.isWorkReplayDateNavigationLocked()) {
      this.promptWorkReplayDateUpgrade();
      return;
    }
    this.selectedDate = this.clampToAllowedWorkReplayDate(date);
    if (!this.userId) return;

    const dateStr = this.selectedDate
      ? this.datePipe.transform(this.selectedDate, "yyyy-MM-dd")
      : null;

    // Reset conduct filters and conduct cache when date changes
    this.showConductLogsCtrl.setValue(true, { emitEvent: false });
    this.conductActivityFilterCtrl.setValue('all', { emitEvent: false });
    this.projectFilterCtrl.setValue('all', { emitEvent: false });
    this.conductTimelogs = [];
    this.availableProjects = [];
    this.hoveredConductTooltipData = null;

    this.pendingFocusTime = null;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        date: dateStr ?? null,
        showConduct: null,
        conductActivity: null,
        project: null,
        time: null,
      },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });

    if (this.shiftContextLoaded) {
      this.loadWorkReplayData();
    }
  }

  setBucketSize(m: BucketSizeMinutes): void {
    this.bucketSize = m;
    this.updateQueryParams();
    this.rebuildBuckets();
  }

  toggleBucket(key: string): void {
    if (this.expandedBucketKeys.has(key)) {
      this.expandedBucketKeys.delete(key);
      return;
    }
    // Enforce max 5 expanded buckets to balance readability with performance.
    if (this.expandedBucketKeys.size >= 5) {
      const oldest = Array.from(this.expandedBucketKeys.values())[0];
      if (oldest) {
        this.expandedBucketKeys.delete(oldest);
      }
    }
    this.expandedBucketKeys.add(key);
  }

  isBucketExpanded(key: string): boolean {
    return this.expandedBucketKeys.has(key);
  }

  trackBucket = (_: number, b: TimelineBucket) => b.key;
  trackScreenshot = (_: number, ss: ScreenshotItem) => ss?.id ?? ss?.image_url ?? _;

  clearReplayFilters(): void {
    this.conductActivityFilterCtrl.setValue("all", { emitEvent: false });
    this.projectFilterCtrl.setValue("all", { emitEvent: false });
    this.appFilterCtrl.setValue("all", { emitEvent: false });
    this.typeFilterCtrl.setValue("all", { emitEvent: false });
    this.daypartFilterCtrl.setValue([], { emitEvent: false });
    this.showConductLogsCtrl.setValue(true, { emitEvent: false });
    this.updateQueryParams();
    this.applyReplayFilters();
  }

  toggleFiltersCollapsed(): void {
    this.filtersCollapsed = !this.filtersCollapsed;
  }

  mapTypeLabel(type: string | undefined | null): string {
    const val = (type || "").trim().toLowerCase();
    if (val === "periodic") return "Periodic";
    if (val === "app-changed") return "App Switch";
    if (val === "admin-request") return "On Demand";
    if (!val) return "Unknown";
    return val
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }

  /** Same chip modifier classes as Screenshots / `app-ss-card`. */
  getTriggerChipClass(type: string | undefined | null): string {
    switch ((type || "").trim().toLowerCase()) {
      case "app-changed":
        return "screenshot-type-chip--appswitch";
      case "admin-request":
        return "screenshot-type-chip--ondemand";
      default:
        return "screenshot-type-chip--periodic";
    }
  }

  onAppIconError(ss: ScreenshotItem | null | undefined): void {
    if (ss) {
      ss._appIconErr = true;
    }
  }

  getScreenshotImageSrc(ss: ScreenshotItem): string {
    return ss?.image_url || ss?.large || ss?.thumbnail || "";
  }

  onScreenshotImageError(ss: ScreenshotItem): void {
    ss._imgError = true;
  }

  getSummaryCountByType(type: string): number {
    return this.filteredScreenshots.filter((s) => (s?.type || "").toLowerCase() === type.toLowerCase()).length;
  }

  get topCapturedApp(): string {
    if (!this.filteredScreenshots.length) return "—";
    const counts = new Map<string, number>();
    for (const s of this.filteredScreenshots) {
      const app = (s?.app_name || "Unknown app").trim() || "Unknown app";
      counts.set(app, (counts.get(app) || 0) + 1);
    }
    let top = "—";
    let topCount = -1;
    counts.forEach((c, app) => {
      if (c > topCount) {
        topCount = c;
        top = app;
      }
    });
    return top;
  }

  get hasActiveFilters(): boolean {
    const isConduct = !!this.showConductLogsCtrl.value;
    return (isConduct && (this.projectFilterCtrl.value || "all") !== "all")
      || (isConduct && (this.conductActivityFilterCtrl.value || "all") !== "all")
      || (this.appFilterCtrl.value || "all") !== "all"
      || (this.typeFilterCtrl.value || "all") !== "all"
      || !this.isDaypartFilterShowingAll;
  }

  get noResultAfterFilter(): boolean {
    if (this.loadingScreenshots) return false;
    if (this.showConductLogsCtrl.value && this.conductActivityFilterCtrl.value === "untracked") {
      return this.bucketGroups.length === 0 && this.hasActiveFilters;
    }
    return !this.loadingScreenshots && this.screenshots.length > 0 && this.filteredScreenshots.length === 0 && this.hasActiveFilters;
  }

  get noResultAfterDaypartFilter(): boolean {
    return (
      !this.loadingScreenshots &&
      this.bucketGroups.length > 0 &&
      this.visibleBucketGroups.length === 0 &&
      !this.isDaypartFilterShowingAll
    );
  }

  get shiftHoursSummary(): string | null {
    const s = this.shiftInfo;
    const tz = this.shiftTimezone;
    if (!s || !s.start_time || !s.end_time) return null;
    const start = String(s.start_time).substring(0, 5);
    const end = String(s.end_time).substring(0, 5);
    if (!start || !end) return null;
    const overnight = s.is_overnight ? " (+1 day)" : "";
    const tzLabel = tz ? ` · ${tz}` : "";
    const name = s.shift_name ? `${s.shift_name} · ` : "";
    return `${name}${start}–${end}${overnight}${tzLabel}`;
  }

  getBucketTopApp(bucket: TimelineBucket): string {
    if (!bucket?.screenshots?.length) return "Unknown app";
    const counts = new Map<string, number>();
    for (const s of bucket.screenshots) {
      const app = (s?.app_name || "Unknown app").trim() || "Unknown app";
      counts.set(app, (counts.get(app) || 0) + 1);
    }
    let top = "Unknown app";
    let n = -1;
    counts.forEach((v, k) => {
      if (v > n) {
        n = v;
        top = k;
      }
    });
    return top;
  }

  getBucketTopAppDisplay(bucket: TimelineBucket): string {
    const top = this.getBucketTopApp(bucket);
    if (!top) return "—";
    const norm = top.toLowerCase().trim();
    if (norm === "unknown app" || norm === "unknown") return "—";
    return top;
  }

  getBucketTypeBreakdown(bucket: TimelineBucket): string {
    const counts = new Map<string, number>();
    for (const s of bucket?.screenshots || []) {
      const t = this.mapTypeLabel(s?.type);
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([k, v]) => `${v} ${k}`)
      .join(", ");
  }

  getBucketStateLabel(bucket: TimelineBucket): string {
    if (this.showConductLogsCtrl.value && bucket?.conductTasks?.length && (!bucket?.screenshots?.length)) {
      return "Untracked Conduct";
    }
    switch (bucket.state) {
      case "active":
        return "Usage";
      case "idle":
        return "Idle";
      case "mixed":
        return "Usage + Idle";
      case "active_no_screenshot":
        return "Tracked (No Screenshot)";
      default:
        return "No Activity";
    }
  }

  getBucketStateClass(bucket: TimelineBucket): string {
    if (this.showConductLogsCtrl.value && bucket?.conductTasks?.length && (!bucket?.screenshots?.length)) {
      return "wr-state-chip--untracked-conduct";
    }
    return `wr-state-chip--${bucket.state}`;
  }

  getBucketStateDotClass(bucket: TimelineBucket): string {
    if (this.showConductLogsCtrl.value && bucket?.conductTasks?.length && (!bucket?.screenshots?.length)) {
      return "wr-state-dot--untracked-conduct";
    }
    return `wr-state-dot--${bucket.state}`;
  }

  toggleDaypart(label: string): void {
    if (this.collapsedDayparts.has(label)) {
      this.collapsedDayparts.delete(label);
      return;
    }
    this.collapsedDayparts.add(label);
  }

  isDaypartCollapsed(label: string): boolean {
    return this.collapsedDayparts.has(label);
  }

  // ── Day navigation ──────────────────────────────────────────────────────────

  get canGoPrevDay(): boolean {
    if (!this.selectedDate) return false;
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() - 1);
    if (!this.minDate) return true;
    return d.getTime() >= new Date(this.minDate).getTime();
  }

  get canGoNextDay(): boolean {
    if (!this.selectedDate) return false;
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() + 1);
    if (!this.maxDate) return true;
    return d.getTime() <= new Date(this.maxDate).getTime();
  }

  prevDay(): void {
    if (this.isWorkReplayDateNavigationLocked()) {
      this.promptWorkReplayDateUpgrade();
      return;
    }
    if (!this.selectedDate || !this.canGoPrevDay) return;
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() - 1);
    this.onDateChanged(d);
  }

  nextDay(): void {
    if (this.isWorkReplayDateNavigationLocked()) {
      this.promptWorkReplayDateUpgrade();
      return;
    }
    if (!this.selectedDate || !this.canGoNextDay) return;
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() + 1);
    this.onDateChanged(d);
  }

  // ── Day totals ───────────────────────────────────────────────────────────────

  get totalUsageFormatted(): string {
    const sec = this.flattenedLogSessions.reduce((s, l) => s + (l.usageSeconds || 0), 0);
    return this.formatDurSec(sec);
  }

  get totalIdleFormatted(): string {
    const sec = this.flattenedLogSessions.reduce((s, l) => s + (l.idleSeconds || 0), 0);
    return this.formatDurSec(sec);
  }

  private formatDurSec(sec: number): string {
    if (!sec || sec <= 0) return '—';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }

  // ── Employee context ─────────────────────────────────────────────────────────

  get selectedEmployeeName(): string {
    if (!this.userId) return '';
    const emp = (this.employees || []).find((e: any) => String(e?.id) === String(this.userId));
    return emp?.name || (this.userDetails as any)?.name || '';
  }

  readonly sysConfig = sysConfig;

  get showRustAgentBetaBadge(): boolean {
    if (!sysConfig.rustAgentBetaBadgeEnabled || !this.userId) {
      return false;
    }
    const emp = (this.employees || []).find((e: any) => String(e?.id) === String(this.userId));
    if (isRustAgentUser(emp) || isRustAgentUser(this.userDetails)) {
      return true;
    }
    const fbUser = this.messagingService
      .getLatestFirebaseUsersSnapshot()
      .find((u) => String(u?.id) === String(this.userId));
    return isRustAgentUser(fbUser);
  }

  // ── Day-part icon ────────────────────────────────────────────────────────────

  getDaypartIcon(label: string): string {
    return this.matIconForDaypartFilter(label);
  }

  matIconForDaypartFilter(value: string | null | undefined): string {
    switch (value) {
      case "all":
        return "schedule";
      case "Late Night":
        return "bedtime";
      case "Morning":
        return "wb_sunny";
      case "Afternoon":
        return "partly_cloudy_day";
      case "Evening":
        return "wb_twilight";
      case "Night":
        return "nights_stay";
      default:
        return "schedule";
    }
  }

  /** Clock-hour segment in company shift TZ (full day runs 12:00 AM → 11:59 PM). */
  private dayPartLabelForHour(hour: number): string {
    if (hour >= 0 && hour < 5) {
      return "Late Night";
    }
    if (hour >= 5 && hour < 12) {
      return "Morning";
    }
    if (hour >= 12 && hour < 17) {
      return "Afternoon";
    }
    if (hour >= 17 && hour < 22) {
      return "Evening";
    }
    return "Night";
  }

  getBucketTimeSummary(bucket: TimelineBucket): string {
    const usage = Math.round((bucket.usageSeconds || 0) / 60);
    const idle = Math.round((bucket.idleSeconds || 0) / 60);
    const parts: string[] = [];
    if (usage > 0) parts.push(`${usage}m usage`);
    if (idle > 0) parts.push(`${idle}m idle`);
    return parts.join(' • ');
  }

  getBucketScreenshotCountText(bucket: TimelineBucket): string {
    const count = bucket?.screenshots?.length || 0;
    if (count <= 0) return "No screenshots";
    return `${count} screenshot${count === 1 ? "" : "s"}`;
  }

  getBucketSecondaryMeta(bucket: TimelineBucket): string {
    if (!bucket?.screenshots?.length) {
      if (this.showConductLogsCtrl.value && bucket?.conductTasks?.length) {
        return "Logged on Conduct (Untracked)";
      }
      return "";
    }
    const parts: string[] = [];
    const app = this.getBucketTopAppDisplay(bucket);
    if (app && app !== "—") parts.push(app);
    const typeBreakdown = this.getBucketTypeBreakdown(bucket);
    if (typeBreakdown) parts.push(typeBreakdown);
    return parts.join(" • ");
  }

  openScreenshotPreview(ss: ScreenshotItem, bucket: TimelineBucket): void {
    const list = Array.isArray(bucket?.screenshots) ? bucket.screenshots : [];
    if (!list.length) {
      return;
    }
    const startIndex = Math.max(
      0,
      list.findIndex((x) => (x?.id != null && ss?.id != null ? String(x.id) === String(ss.id) : x === ss))
    );
    const u = this.userDetails;
    this.screenshotFancybox.open({
      items: list,
      startIndex,
      showWindow: true,
      showConduct: !!this.showConductLogsCtrl.value,
      shiftTimezone: this.shiftTimezone,
      employee: u
        ? {
            name: u.name || u.full_name,
            avatar: u.profile_image_url || u.image_url || u.image || u.avatar,
          }
        : null,
    });
  }

  private loadEmployeeDetails(userId: string): void {
    this.loadingUser = true;
    const requestId = ++this.employeeDetailsRequestId;
    this.subs.add(
      this.projectService.getEmployeeData(userId).subscribe({
        next: (data: any) => {
          if (requestId !== this.employeeDetailsRequestId || this.userId !== String(userId)) {
            return;
          }
          this.userDetails = data?.user || data || null;
          this.loadingUser = false;
        },
        error: () => {
          if (requestId !== this.employeeDetailsRequestId || this.userId !== String(userId)) {
            return;
          }
          this.userDetails = null;
          this.loadingUser = false;
        },
      })
    );
  }

  private loadWorkReplayData(): void {
    if (!this.userId || !this.selectedDate) {
      return;
    }
    this.loadScreenshotsForSelectedDay();
    this.loadLogsForSelectedDay();
    this.loadConductLogsForSelectedDay();
  }

  private loadScreenshotsForSelectedDay(): void {
    if (!this.userId) return;
    const bounds = this.getSelectedDayBoundsForApi();
    if (!bounds) return;

    this.loadingScreenshots = true;
    this.screenshots = [];
    this.filteredScreenshots = [];
    this.screenshotsTotal = 0;
    this.screenshotsLoaded = 0;
    this.availableApps = [];
    this.expandedBucketKeys.clear();

    const perPage = 100;
    const firstPage = 1;
    this.subs.add(
      this.projectService
        .getSingleSS(
          this.userId,
          perPage,
          firstPage,
          bounds.start,
          bounds.end,
          WORK_REPLAY_SCREENSHOT_CONTEXT
        )
        .subscribe({
        next: (res: HttpResponse<any>) => {
          const firstData = ((res?.body as any)?.data || []) as any[];
          const total = Number(res?.headers?.get("X-WP-Total") || firstData.length || 0);
          const totalPages = Math.max(1, Math.ceil(total / perPage));
          if (totalPages <= 1) {
            this.finalizeScreenshotsLoad(firstData, total);
            return;
          }

          const requests = [];
          for (let page = 2; page <= totalPages; page++) {
            requests.push(
              this.projectService
                .getSingleSS(
                  this.userId!,
                  perPage,
                  page,
                  bounds.start,
                  bounds.end,
                  WORK_REPLAY_SCREENSHOT_CONTEXT
                )
                .pipe(catchError(() => of(null)))
            );
          }

          this.subs.add(
            forkJoin(requests).subscribe((rest: any[]) => {
              const all = [...firstData];
              for (const r of rest) {
                const pageData = ((r?.body as any)?.data || []) as any[];
                all.push(...pageData);
              }
              this.finalizeScreenshotsLoad(all, total);
            })
          );
        },
        error: (err) => this.handleScreenshotsLoadError(err),
      })
    );
  }

  private handleScreenshotsLoadError(err: unknown): void {
    const httpErr = err as HttpErrorResponse;
    const body = httpErr?.error;
    const apiMessage =
      body && typeof body === "object" && typeof (body as { message?: string }).message === "string"
        ? (body as { message: string }).message
        : null;
    const apiCode =
      body && typeof body === "object" && (body as { code?: string }).code
        ? String((body as { code: string }).code)
        : "";

    const showUpgrade =
      this.isWorkReplayPreviewOnly() ||
      httpErr?.status === 403 ||
      /plan|upgrade|preview|entitlement|work_replay|yesterday/i.test(apiMessage || "") ||
      /plan|upgrade|preview|entitlement|work_replay|yesterday/i.test(apiCode);

    this.promptWorkReplayUpgradeDialog({
      title: showUpgrade ? "Upgrade for full history" : "Could not load screenshots",
      message:
        apiMessage ||
        "Could not load screenshots for this date. Try again or choose another day.",
      confirmLabel: showUpgrade ? "View plans" : "OK",
      navigateOnConfirm: showUpgrade,
    });

    this.screenshots = [];
    this.filteredScreenshots = [];
    this.screenshotsTotal = 0;
    this.screenshotsLoaded = 0;
    this.availableApps = [];
    this.buckets = [];
    this.bucketGroups = [];
    this.loadingScreenshots = false;
  }

  private decorateScreenshot(s: any): ScreenshotItem {
    const raw = s?.ist_time ?? s?.utc_time ?? s?.captured_at ?? s?.captured_time;
    const utc = this.screenshotTime.parseCaptureToUtc(raw, this.shiftTimezone);
    const ms = utc && utc.isValid ? utc.toMillis() : null;
    const base = this.screenshotTime.getEnrichedScreenshotTime(raw, this.shiftTimezone);
    const wrTime = this.enrichWorkReplayScreenshotTime(raw, base);
    return {
      ...s,
      captured_at: s?.ist_time || s?.captured_at,
      captured_time: s?.ist_time || s?.captured_time,
      _parsedUtcMs: ms,
      _windowName: null,
      _matchConfidence: "no_match",
      _imgError: false,
      ...(wrTime ?? {}),
    };
  }

  /**
   * Work Replay: primary line is shift (office) wall time; viewer time is secondary when it differs.
   */
  private enrichWorkReplayScreenshotTime(
    raw: string | number | Date | null | undefined,
    base: ScreenshotTimeEnrichment | null
  ): ScreenshotTimeEnrichment | null {
    if (!base) {
      return null;
    }
    if (!this.shiftTimezone || !this.screenshotTime.isValidIanaZone(this.shiftTimezone)) {
      return base;
    }
    const utc = this.screenshotTime.parseCaptureToUtc(raw, this.shiftTimezone);
    if (!utc?.isValid) {
      return base;
    }

    const officePrimary = this.formatInstantInZone(utc.toMillis(), this.shiftTimezone);
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const viewerWall = this.formatInstantInZone(utc.toMillis(), viewerZone);
    let secondaryLine: string | undefined;
    if (viewerWall !== officePrimary) {
      secondaryLine = `Your time · ${viewerWall}`;
    }

    const timeDisplay: ScreenshotTimeView = {
      primaryAbsolute: officePrimary,
      secondaryLine,
      tooltip: this.screenshotTime.buildCardHoverTooltip(raw, this.shiftTimezone),
    };

    return { ...base, timeDisplay };
  }

  private formatInstantInZone(utcMs: number, ianaZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: ianaZone,
    }).format(new Date(utcMs));
  }

  private loadLogsForSelectedDay(): void {
    if (!this.userId || !this.selectedDate) return;

    const ymd = this.datePipe.transform(this.selectedDate, "yyyy-MM-dd");
    if (!ymd) return;

    this.loadingLogs = true;
    this.flattenedLogSessions = [];

    this.subs.add(
      this.projectService.getUserApplicationLogs(this.userId, ymd, ymd).subscribe((res: any) => {
        this.logsByDate = res;
        this.flattenedLogSessions = this.flattenSessions(res);
        this.matchScreenshotsToLogs();
        this.applyReplayFilters();
        this.loadingLogs = false;
      }, () => {
        this.logsByDate = null;
        this.flattenedLogSessions = [];
        this.matchScreenshotsToLogs();
        this.applyReplayFilters();
        this.loadingLogs = false;
      })
    );
  }

  private flattenSessions(raw: any): Array<{ inUtcMs: number; outUtcMs: number; appName?: string; windowName?: string; usageSeconds?: number; idleSeconds?: number }> {
    const out: Array<{ inUtcMs: number; outUtcMs: number; appName?: string; windowName?: string; usageSeconds?: number; idleSeconds?: number }> = [];
    if (!raw || typeof raw !== "object") return out;

    for (const key of Object.keys(raw)) {
      const day = raw[key];
      const sessions = Array.isArray(day?.sessions) ? day.sessions : [];
      for (const s of sessions) {
        const inUtc = this.screenshotTime.parseCaptureToUtc(s?.in, this.shiftTimezone);
        const outUtc = this.screenshotTime.parseCaptureToUtc(s?.out, this.shiftTimezone);
        if (!inUtc?.isValid || !outUtc?.isValid) continue;
        out.push({
          inUtcMs: inUtc.toMillis(),
          outUtcMs: outUtc.toMillis(),
          appName: s?.appName,
          windowName: s?.windowName,
          usageSeconds: this.parseDurationToSeconds(s?.usage),
          idleSeconds: this.parseDurationToSeconds(s?.idle),
        });
      }
    }

    out.sort((a, b) => a.inUtcMs - b.inUtcMs);
    return out;
  }

  private matchScreenshotsToLogs(): void {
    if (!Array.isArray(this.screenshots) || this.screenshots.length === 0) return;
    const logs = this.flattenedLogSessions;
    if (!Array.isArray(logs) || logs.length === 0) {
      this.screenshots = this.screenshots.map(s => ({ ...s, _windowName: null, _matchConfidence: "no_match" }));
      return;
    }

    const toleranceMs = 3 * 60 * 1000;
    this.screenshots = this.screenshots.map((ss) => {
      const t = ss._parsedUtcMs;
      if (!t) {
        return { ...ss, _windowName: null, _matchConfidence: "no_match" };
      }

      // 1) Containing interval wins.
      const containing = logs.find((l) => t >= l.inUtcMs && t <= l.outUtcMs);
      if (containing) {
        return { ...ss, _windowName: containing.windowName || null, _matchConfidence: "exact_match" };
      }

      // 2) Nearest within tolerance.
      let best: any = null;
      let bestDelta = Number.POSITIVE_INFINITY;
      for (const l of logs) {
        const delta = Math.min(Math.abs(t - l.inUtcMs), Math.abs(t - l.outUtcMs));
        if (delta < bestDelta) {
          best = l;
          bestDelta = delta;
        }
      }
      if (best && bestDelta <= toleranceMs) {
        return { ...ss, _windowName: best.windowName || null, _matchConfidence: "near_match" };
      }

      return { ...ss, _windowName: null, _matchConfidence: "no_match" };
    });
  }

  private rebuildBuckets(): void {
    this.buckets = this.buildBucketsForSelectedDay(
      this.filteredScreenshots,
      this.bucketSize,
      !!this.showEmptySlotsCtrl.value
    );
    this.bucketGroups = this.groupBucketsByDayPart(this.buckets);
    this.seedExpandedBuckets();
  }

  private applyReplayFilters(): void {
    const appFilter = (this.appFilterCtrl.value || "all").toLowerCase();
    const typeFilter = (this.typeFilterCtrl.value || "all").toLowerCase();
    const isConductEnabled = !!this.showConductLogsCtrl.value;
    const projectFilter = isConductEnabled ? (this.projectFilterCtrl.value || "all").toLowerCase() : "all";
    const activityFilter = isConductEnabled ? (this.conductActivityFilterCtrl.value || "all").toLowerCase() : "all";

    this.filteredScreenshots = (this.screenshots || []).filter((s) => {
      const app = (s?.app_name || "").toLowerCase();
      const type = (s?.type || "").toLowerCase();
      const project = (s?._conductProject || "").toLowerCase();
      const isTracked = !!s?._isConductTracked;

      if (appFilter !== "all" && app !== appFilter) return false;
      if (typeFilter !== "all" && type !== typeFilter) return false;

      if (activityFilter === "tracked") {
        if (!isTracked) return false;
        if (projectFilter !== "all" && project !== projectFilter) return false;
      } else if (activityFilter === "untracked") {
        // Untracked conduct activity has no captured screenshots
        return false;
      } else {
        if (projectFilter !== "all" && project !== projectFilter) return false;
      }

      return true;
    });

    this.rebuildBuckets();
  }

  private refreshAvailableAppsFromLoadedScreenshots(): void {
    this.availableApps = Array.from(
      new Set(
        (this.screenshots || [])
          .map((s) => (s?.app_name || "").trim())
          .filter((v) => !!v)
      )
    ).sort((a, b) => a.localeCompare(b));
  }

  private finalizeScreenshotsLoad(rawRows: any[], total: number): void {
    const seen = new Set<string>();
    const deduped: any[] = [];
    for (const row of rawRows || []) {
      const id = String(row?.id ?? "");
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      deduped.push(row);
    }
    this.screenshotsTotal = Math.max(total, deduped.length);
    this.screenshots = deduped.map((s) => this.decorateScreenshot(s));
    this.screenshotsLoaded = this.screenshots.length;
    this.matchScreenshotsToLogs();
    this.matchScreenshotsToConductLogs();
    this.refreshAvailableAppsFromLoadedScreenshots();

    const currentApp = this.appFilterCtrl.value;
    if (currentApp && currentApp !== "all") {
      const isAppAvailable = this.availableApps.some(app => app.toLowerCase() === currentApp.toLowerCase());
      if (!isAppAvailable) {
        this.appFilterCtrl.setValue("all", { emitEvent: false });
        this.updateQueryParams();
      }
    }

    this.applyReplayFilters();
    this.loadingScreenshots = false;
  }

  private buildBucketsForSelectedDay(
    items: ScreenshotItem[],
    bucketMinutes: BucketSizeMinutes,
    showEmptySlots: boolean
  ): TimelineBucket[] {
    if (!this.selectedDate) return [];
    const bounds = this.getSelectedDayBoundsForApi();
    if (!bounds) return [];

    const startUtc = this.screenshotTime.parseCaptureToUtc(bounds.start, this.shiftTimezone);
    const endUtc = this.screenshotTime.parseCaptureToUtc(bounds.end, this.shiftTimezone);
    if (!startUtc?.isValid || !endUtc?.isValid) return [];

    const startMs = startUtc.toMillis();
    const endMs = endUtc.toMillis();
    const bucketMs = bucketMinutes * 60 * 1000;

    const map = new Map<string, TimelineBucket>();
    for (let bStart = startMs; bStart <= endMs; bStart += bucketMs) {
      const bEnd = Math.min(endMs, bStart + bucketMs);
      const key = String(bStart);
      map.set(key, {
        key,
        startUtcMs: bStart,
        endUtcMs: bEnd,
        label: this.formatBucketLabel(bStart, bEnd),
        screenshots: [],
        usageSeconds: 0,
        idleSeconds: 0,
        state: "no_data",
      });
    }

    const safeItems = Array.isArray(items) ? items : [];
    for (const ss of safeItems) {
      if (!ss?._parsedUtcMs) continue;
      const t = ss._parsedUtcMs;
      if (t < startMs || t > endMs) continue;
      const idx = Math.floor((t - startMs) / bucketMs);
      const bStart = startMs + idx * bucketMs;
      const key = String(bStart);
      const bucket = map.get(key);
      if (bucket) {
        bucket.screenshots.push(ss);
      }
    }

    const logs = Array.isArray(this.flattenedLogSessions) ? this.flattenedLogSessions : [];
    for (const log of logs) {
      const logStart = Math.max(startMs, log.inUtcMs);
      const logEnd = Math.min(endMs, log.outUtcMs);
      if (logEnd <= logStart) continue;
      const logDuration = Math.max(1, logEnd - logStart);
      const idleSec = Math.max(0, Number(log.idleSeconds || 0));
      const usageSec = Math.max(0, Number(log.usageSeconds || 0));

      for (let bStart = Math.floor((logStart - startMs) / bucketMs) * bucketMs + startMs; bStart <= logEnd; bStart += bucketMs) {
        const key = String(bStart);
        const bucket = map.get(key);
        if (!bucket) continue;
        const overlapStart = Math.max(bucket.startUtcMs, logStart);
        const overlapEnd = Math.min(bucket.endUtcMs, logEnd);
        if (overlapEnd <= overlapStart) continue;
        const overlapMs = overlapEnd - overlapStart;
        const ratio = overlapMs / logDuration;
        bucket.usageSeconds += usageSec * ratio;
        bucket.idleSeconds += idleSec * ratio;
      }
    }

    const buckets = Array.from(map.values()).sort((a, b) => a.startUtcMs - b.startUtcMs);
    buckets.forEach((b) => b.screenshots.sort((a, c) => (a._parsedUtcMs || 0) - (c._parsedUtcMs || 0)));

    // Associate Conduct tasks with overlapping buckets ONLY when showConductLogsCtrl is enabled
    if (this.showConductLogsCtrl.value) {
      const ymd = this.datePipe.transform(this.selectedDate, "yyyy-MM-dd") || '';
      const parsedConduct = (this.conductTimelogs || [])
        .map((l: any) => ({
          raw: l,
          interval: this.parseConductInterval(l, ymd),
          projectName: (l.project_name || 'General Project').trim(),
          taskTitle: (l.task_title || 'General Task').trim()
        }))
        .filter((x: any) => x.interval !== null);

      if (parsedConduct.length > 0) {
        buckets.forEach((b) => {
          const overlapping = parsedConduct.filter((p: any) => {
            return p.interval.startUtcMs < b.endUtcMs && p.interval.endUtcMs > b.startUtcMs;
          });
          if (overlapping.length > 0) {
            b.conductTasks = overlapping.map((p: any) => {
              const fullDurSec = parseInt(String(p.raw.duration_time_second), 10) || 0;
              const overlapStart = Math.max(b.startUtcMs, p.interval.startUtcMs);
              const overlapEnd = Math.min(b.endUtcMs, p.interval.endUtcMs);
              const overlapSec = Math.max(0, Math.round((overlapEnd - overlapStart) / 1000));
              const totalSpanSec = Math.max(1, Math.round((p.interval.endUtcMs - p.interval.startUtcMs) / 1000));
              const ratio = Math.min(1, overlapSec / totalSpanSec);
              const slotDurationSeconds = fullDurSec > 0
                ? Math.max(1, Math.min(fullDurSec, Math.round(fullDurSec * ratio)))
                : overlapSec;

              const timeRange = this.formatConductTimeRange(
                p.interval.startUtcMs,
                p.interval.endUtcMs,
                p.raw.from_time,
                p.raw.end_time
              );

              return {
                projectName: p.projectName,
                taskTitle: p.taskTitle,
                durationSeconds: slotDurationSeconds,
                totalDurationSeconds: fullDurSec,
                workedHours: this.formatHoursMinutes(slotDurationSeconds),
                totalWorkedHours: this.formatHoursMinutes(fullDurSec),
                timeRange,
                fromTime: p.raw.from_time,
                endTime: p.raw.end_time
              };
            });
            b.conductTotalSeconds = b.conductTasks.reduce((sum: number, t: any) => sum + t.durationSeconds, 0);
          }
        });
      }
    }

    const isConductEnabled = !!this.showConductLogsCtrl.value;
    const projectFilter = isConductEnabled ? (this.projectFilterCtrl.value || "all").toLowerCase() : "all";
    const activityFilter = isConductEnabled ? (this.conductActivityFilterCtrl.value || "all").toLowerCase() : "all";

    buckets.forEach((b) => {
      const hasShots = b.screenshots.length > 0;
      const hasUsage = b.usageSeconds > 0;
      const hasIdle = b.idleSeconds > 0;
      const hasConduct = isConductEnabled && (b.conductTasks?.length || 0) > 0;

      if (!hasShots && !hasUsage && !hasIdle) {
        b.state = hasConduct ? "active_no_screenshot" : "no_data";
      } else if (!hasShots && (hasUsage || hasIdle)) {
        b.state = "active_no_screenshot";
      } else if (hasUsage && hasIdle) {
        b.state = "mixed";
      } else if (hasIdle && !hasUsage) {
        b.state = "idle";
      } else {
        b.state = "active";
      }
    });

    // Helper: checks if bucket has conduct tasks matching active project filter
    const hasMatchingConduct = (b: TimelineBucket): boolean => {
      if (!isConductEnabled || !b.conductTasks || b.conductTasks.length === 0) return false;
      if (projectFilter === "all") return true;
      return b.conductTasks.some((t: ConductBucketTask) => (t.projectName || "").toLowerCase() === projectFilter);
    };

    // Helper: checks if any screenshot was captured during Conduct timelog in this bucket
    const hasConductScreenshots = (b: TimelineBucket): boolean => {
      return (this.screenshots || []).some((s) => {
        const t = s._parsedUtcMs;
        if (!t || t < b.startUtcMs || t >= b.endUtcMs) return false;
        if (!s._isConductTracked) return false;
        if (projectFilter !== "all" && (s._conductProject || "").toLowerCase() !== projectFilter) return false;
        return true;
      });
    };

    if (activityFilter === "tracked") {
      // Tracked activity: screenshots captured AND conduct timelogs logged for that time
      return buckets.filter((b) => hasMatchingConduct(b) && hasConductScreenshots(b));
    }

    if (activityFilter === "untracked") {
      // Untracked activity: screenshots NOT captured but conduct timelogs are logged
      return buckets.filter((b) => hasMatchingConduct(b) && !hasConductScreenshots(b));
    }

    if (projectFilter !== "all") {
      return buckets.filter(
        (b) =>
          (b.screenshots?.length || 0) > 0 ||
          hasMatchingConduct(b)
      );
    }

    if (showEmptySlots) {
      return buckets;
    }
    // Compact view: keep slots that have screenshots, conduct tasks (if enabled), or tracked usage/idle.
    return buckets.filter(
      (b) =>
        (b.screenshots?.length || 0) > 0 ||
        (isConductEnabled && (b.conductTasks?.length || 0) > 0) ||
        b.usageSeconds > 0 ||
        b.idleSeconds > 0
    );
  }

  private formatBucketLabel(startUtcMs: number, endUtcMs: number): string {
    const tz = this.shiftTimezone && this.screenshotTime.isValidIanaZone(this.shiftTimezone) ? this.shiftTimezone : undefined;
    const start = new Date(startUtcMs);
    const end = new Date(endUtcMs);
    const fmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", timeZone: tz });
    return `${fmt.format(start)} – ${fmt.format(end)}`;
  }

  private groupBucketsByDayPart(
    buckets: TimelineBucket[]
  ): Array<{ label: string; buckets: TimelineBucket[]; bucketCount: number; screenshotCount: number }> {
    const groups = new Map<string, { buckets: TimelineBucket[]; screenshotCount: number }>();
    const tz = this.shiftTimezone && this.screenshotTime.isValidIanaZone(this.shiftTimezone) ? this.shiftTimezone : null;
    for (const b of buckets) {
      const hour = tz
        ? DateTime.fromMillis(b.startUtcMs, { zone: "utc" }).setZone(tz).hour
        : new Date(b.startUtcMs).getHours();
      const label = this.dayPartLabelForHour(hour);
      const entry = groups.get(label) || { buckets: [], screenshotCount: 0 };
      entry.buckets.push(b);
      entry.screenshotCount += (b.screenshots?.length || 0);
      groups.set(label, entry);
    }
    const order: DaypartLabel[] = [...DAYPART_LABELS];
    return order
      .filter((k) => groups.has(k))
      .map((k) => {
        const entry = groups.get(k)!;
        const sorted = entry.buckets.sort((a, c) => a.startUtcMs - c.startUtcMs);
        return { label: k, buckets: sorted, bucketCount: sorted.length, screenshotCount: entry.screenshotCount };
      });
  }

  private seedExpandedBuckets(): void {
    if (this.pendingFocusTime && this.selectedDate) {
      const ymd = this.datePipe.transform(this.selectedDate, "yyyy-MM-dd");
      if (ymd) {
        const targetUtc = this.parseConductTimeToUtc(this.pendingFocusTime, ymd);
        if (targetUtc?.isValid) {
          const targetMs = targetUtc.toMillis();
          const matchingBucket = this.buckets.find(
            (b) => targetMs >= b.startUtcMs && targetMs < b.endUtcMs
          ) || this.buckets.find(
            (b) => targetMs >= b.startUtcMs && targetMs <= b.endUtcMs
          );

          if (matchingBucket) {
            this.expandedBucketKeys.clear();
            this.expandedBucketKeys.add(matchingBucket.key);

            for (const group of this.bucketGroups) {
              if (group.buckets.some((b) => b.key === matchingBucket.key)) {
                this.collapsedDayparts.delete(group.label);
                break;
              }
            }

            const targetKey = matchingBucket.key;
            setTimeout(() => {
              const el = document.getElementById(`bucket-${targetKey}`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 150);

            this.pendingFocusTime = null;
            return;
          }
        }
      }
    }

    // Scan-first: auto-expand the first 2 buckets that actually have screenshots.
    // Still respects the "max 2 expanded buckets" performance guardrail.
    if (this.expandedBucketKeys.size > 0) {
      // Respect user interactions if they've already expanded/collapsed.
      return;
    }
    const withShots = this.buckets.filter((b) => (b.screenshots?.length || 0) > 0);
    for (const b of withShots.slice(0, 2)) {
      this.expandedBucketKeys.add(b.key);
    }
  }

  private parseDurationToSeconds(input: any): number {
    if (!input || typeof input !== "string") return 0;
    const parts = input.split(":").map((x: string) => Number(x));
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return 0;
    return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
  }

  /**
   * Full calendar day for the selected date: 00:00:00–23:59:59 in company shift timezone
   * (or browser-local naive bounds when no shift is configured).
   */
  private getSelectedDayBoundsForApi(): { start: string; end: string } | null {
    if (!this.selectedDate) return null;

    const ymd = this.datePipe.transform(this.selectedDate, "yyyy-MM-dd");
    if (!ymd) return null;

    const startM = moment(ymd, "YYYY-MM-DD").startOf("day");
    const endM = moment(ymd, "YYYY-MM-DD").endOf("day");
    const range = this.screenshotTime.filterBoundsForApi(startM, endM, this.shiftTimezone);
    return { start: range.start, end: range.end };
  }

  private loadConductLogsForSelectedDay(): void {
    if (!this.userId || !this.selectedDate) return;
    const ymd = this.datePipe.transform(this.selectedDate, "yyyy-MM-dd");
    if (!ymd) return;

    const user = this.tokenEncryptionService.getEncryptedItem<any>('user');
    const loggedInUser = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const companyId = Number(loggedInUser?.company_id ?? user?.company_id ?? user?.id ?? 0);
    if (!companyId) return;

    const loggedInUserId = loggedInUser?.id ?? user?.id;
    const currentUserId = this.userId;
    this.loadingConduct = true;

    const onLogsLoaded = (rawLogs: any[]) => {
      if (this.userId !== currentUserId) return;
      this.loadingConduct = false;
      const logs = Array.isArray(rawLogs) ? rawLogs : [];

      const targetUserIds = new Set<string>();
      if (this.userId != null) targetUserIds.add(String(this.userId));
      if (this.userDetails?.id != null) targetUserIds.add(String(this.userDetails.id));

      const targetNames = new Set<string>();
      if (this.userDetails?.name) targetNames.add(this.userDetails.name.trim().toLowerCase());
      if (this.userDetails?.full_name) targetNames.add(this.userDetails.full_name.trim().toLowerCase());
      if (this.selectedEmployeeName) {
        const cleanName = this.selectedEmployeeName.trim().toLowerCase();
        if (cleanName && cleanName !== 'select employee') targetNames.add(cleanName);
      }

      const emp = (this.employees || []).find((e: any) => String(e?.id) === String(this.userId));
      if (emp?.name) targetNames.add(emp.name.trim().toLowerCase());
      if (emp?.full_name) targetNames.add(emp.full_name.trim().toLowerCase());

      const targetEmails = new Set<string>();
      if (this.userDetails?.email) targetEmails.add(this.userDetails.email.trim().toLowerCase());
      if (this.userDetails?.user_email) targetEmails.add(this.userDetails.user_email.trim().toLowerCase());
      if (emp?.email) targetEmails.add(emp.email.trim().toLowerCase());
      if (emp?.user_email) targetEmails.add(emp.user_email.trim().toLowerCase());

      const userFilteredLogs = logs.filter((log: any) => {
        const logUid = String(log.supersee_user_id || log.supersee_user || log.user_id || '');
        if (logUid && targetUserIds.has(logUid)) return true;
        const logEmpName = (log.employee_name || '').trim().toLowerCase();
        if (logEmpName) {
          if (targetNames.has(logEmpName)) return true;
          const stripped = logEmpName.replace(/[.\-_]+$/g, '').trim();
          if (stripped && targetNames.has(stripped)) return true;
        }
        const logEmpEmail = (log.employee_email || '').trim().toLowerCase();
        if (logEmpEmail && targetEmails.has(logEmpEmail)) return true;
        return false;
      });

      this.conductTimelogs = userFilteredLogs;
      this.refreshAvailableProjectsFromConductLogs();

      this.matchScreenshotsToConductLogs();
      this.applyReplayFilters();
    };

    this.subs.add(
      this.projectService.fetchConductTimelogs({
        supersee_company_id: companyId,
        requesting_user_id: loggedInUserId,
        start_date: ymd,
        end_date: ymd
      }).pipe(
        catchError(() => of(null))
      ).subscribe((res: any) => {
        const rawLogs = res?.timelogs || res?.data || (Array.isArray(res) ? res : []);
        if (rawLogs && rawLogs.length > 0) {
          onLogsLoaded(rawLogs);
        } else {
          this.subs.add(
            this.projectService.getSavedConductTimelogs(companyId, null, null, ymd, ymd).pipe(
              catchError(() => of({ timelogs: [] }))
            ).subscribe((savedRes: any) => {
              const savedLogs = savedRes?.timelogs || savedRes?.data || (Array.isArray(savedRes) ? savedRes : []);
              onLogsLoaded(savedLogs);
            })
          );
        }
      })
    );
  }

  private refreshAvailableProjectsFromConductLogs(): void {
    const map = new Map<string, { totalSeconds: number }>();
    (this.conductTimelogs || []).forEach((log: any) => {
      const pName = (log.project_name || 'General Project').trim();
      if (!pName) return;
      const dur = parseInt(String(log.duration_time_second), 10) || 0;
      const cur = map.get(pName) || { totalSeconds: 0 };
      cur.totalSeconds += dur;
      map.set(pName, cur);
    });

    this.availableProjects = Array.from(map.entries())
      .map(([name, data]) => ({
        name,
        totalSeconds: data.totalSeconds,
        totalHours: this.formatHoursMinutes(data.totalSeconds)
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const curVal = this.projectFilterCtrl.value;
    if (curVal && curVal !== 'all') {
      const exists = this.availableProjects.some(p => p.name.toLowerCase() === curVal.toLowerCase());
      if (!exists) {
        this.projectFilterCtrl.setValue('all', { emitEvent: false });
        this.updateQueryParams();
      }
    }
  }

  private parseConductInterval(log: any, ymd: string): { startUtcMs: number; endUtcMs: number } | null {
    if (!log) return null;
    const fromStr = String(log.from_time || '').trim();
    const endStr = String(log.end_time || '').trim();
    const rawDate = String(log.log_date || log.date || ymd || '').trim();
    let logDate = ymd;
    if (rawDate) {
      const m = moment(rawDate);
      if (m.isValid()) {
        logDate = m.format('YYYY-MM-DD');
      }
    }

    let startUtc: DateTime | null = null;
    let endUtc: DateTime | null = null;

    if (fromStr) {
      startUtc = this.parseConductTimeToUtc(fromStr, logDate);
    }

    if (endStr) {
      endUtc = this.parseConductTimeToUtc(endStr, logDate);
    }

    const durSec = parseInt(String(log.duration_time_second), 10) || 0;

    if (startUtc?.isValid && endUtc?.isValid) {
      let sMs = startUtc.toMillis();
      let eMs = endUtc.toMillis();
      if (eMs <= sMs && durSec > 0) {
        eMs = sMs + durSec * 1000;
      }
      if (eMs > sMs) {
        return {
          startUtcMs: sMs,
          endUtcMs: eMs
        };
      }
    }

    if (startUtc?.isValid && durSec > 0) {
      const sMs = startUtc.toMillis();
      return {
        startUtcMs: sMs,
        endUtcMs: sMs + durSec * 1000
      };
    }

    if (endUtc?.isValid && durSec > 0) {
      const eMs = endUtc.toMillis();
      return {
        startUtcMs: eMs - durSec * 1000,
        endUtcMs: eMs
      };
    }

    return null;
  }

  private parseConductTimeToUtc(timeStr: string, logDate: string): DateTime | null {
    if (!timeStr) return null;
    const tz = this.shiftTimezone && this.screenshotTime.isValidIanaZone(this.shiftTimezone)
      ? this.shiftTimezone
      : Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timeStr.includes('T') || (timeStr.includes('-') && timeStr.length > 10)) {
      const dt = this.screenshotTime.parseCaptureToUtc(timeStr, this.shiftTimezone);
      if (dt?.isValid) return dt;
    }

    const isAmPm = /am|pm/i.test(timeStr);
    if (isAmPm) {
      const m = moment(`${logDate} ${timeStr}`, [
        'YYYY-MM-DD hh:mm:ss A',
        'YYYY-MM-DD hh:mm A',
        'YYYY-MM-DD h:mm:ss A',
        'YYYY-MM-DD h:mm A'
      ]);
      if (m.isValid()) {
        const iso = m.format('YYYY-MM-DDTHH:mm:ss');
        const dt = DateTime.fromISO(iso, { zone: tz });
        if (dt.isValid) return dt.toUTC();
      }
    }

    const m24 = moment(`${logDate} ${timeStr}`, [
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD H:mm:ss',
      'YYYY-MM-DD H:mm'
    ]);
    if (m24.isValid()) {
      const iso = m24.format('YYYY-MM-DDTHH:mm:ss');
      const dt = DateTime.fromISO(iso, { zone: tz });
      if (dt.isValid) return dt.toUTC();
    }

    const fullStr = `${logDate} ${timeStr}`;
    return this.screenshotTime.parseCaptureToUtc(fullStr, this.shiftTimezone);
  }

  formatConductTimeRange(
    startUtcMs?: number | null,
    endUtcMs?: number | null,
    fallbackFrom?: string,
    fallbackEnd?: string
  ): string {
    if (startUtcMs && endUtcMs) {
      return `${this.formatInstant12h(startUtcMs)} – ${this.formatInstant12h(endUtcMs)}`;
    }
    if (fallbackFrom && fallbackEnd) {
      return `${fallbackFrom} – ${fallbackEnd}`;
    }
    return fallbackFrom || fallbackEnd || '';
  }

  formatInstant12h(utcMs: number): string {
    const tz = this.shiftTimezone && this.screenshotTime.isValidIanaZone(this.shiftTimezone)
      ? this.shiftTimezone
      : undefined;
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: tz
    }).format(new Date(utcMs));
  }

  private matchScreenshotsToConductLogs(): void {
    if (!this.conductTimelogs || this.conductTimelogs.length === 0) {
      (this.screenshots || []).forEach(ss => {
        ss._conductProject = null;
        ss._conductTask = null;
        ss._isConductTracked = false;
      });
      return;
    }
    const ymd = this.datePipe.transform(this.selectedDate, "yyyy-MM-dd") || '';
    const parsedLogs = this.conductTimelogs.map(l => ({
      raw: l,
      interval: this.parseConductInterval(l, ymd),
      projectName: (l.project_name || 'General Project').trim(),
      taskTitle: (l.task_title || 'General Task').trim()
    })).filter(x => x.interval !== null);

    (this.screenshots || []).forEach(ss => {
      const t = ss._parsedUtcMs;
      if (!t) {
        ss._conductProject = null;
        ss._conductTask = null;
        ss._isConductTracked = false;
        return;
      }
      const matched = parsedLogs.find(p => t >= p.interval!.startUtcMs && t <= p.interval!.endUtcMs);
      if (matched) {
        ss._conductProject = matched.projectName;
        ss._conductTask = matched.taskTitle;
        ss._isConductTracked = true;
      } else {
        ss._conductProject = null;
        ss._conductTask = null;
        ss._isConductTracked = false;
      }
    });
  }

  getBucketConductSummary(b: TimelineBucket): string {
    if (!b?.conductTasks || b.conductTasks.length === 0) return '';
    const uniqueProjects = Array.from(new Set(b.conductTasks.map(t => t.projectName)));
    if (uniqueProjects.length === 1) {
      const pName = uniqueProjects[0];
      const count = b.conductTasks.length;
      return count === 1 ? pName : `${pName} · ${count} tasks`;
    }
    return `${uniqueProjects.length} projects · ${b.conductTasks.length} tasks`;
  }

  onConductPillMouseEnter(event: MouseEvent, bucket: TimelineBucket): void {
    if (!this.showConductLogsCtrl.value || !bucket.conductTasks || bucket.conductTasks.length === 0) return;

    const targetEl = event.currentTarget as HTMLElement;
    if (!targetEl) return;
    const rect = targetEl.getBoundingClientRect();

    const uniqueProjects = Array.from(new Set(bucket.conductTasks.map(t => t.projectName)));
    const projectName = uniqueProjects.length === 1 ? uniqueProjects[0] : `${uniqueProjects.length} Projects`;

    const totalDurSec = bucket.conductTasks.reduce((sum, t) => sum + (t.durationSeconds || 0), 0);
    const slotLoggedTime = this.formatHoursMinutes(totalDurSec);

    const usageSec = Math.round(bucket.usageSeconds || 0);
    const usageComparison = usageSec > 0 ? this.formatHoursMinutes(usageSec) : null;

    const tasks = bucket.conductTasks.slice(0, 5).map(t => ({
      taskTitle: t.taskTitle || 'General Task',
      timeRange: t.timeRange,
      workedHours: t.workedHours || this.formatHoursMinutes(t.durationSeconds || 0),
      totalWorkedHours: t.totalWorkedHours
    }));

    const moreCount = Math.max(0, bucket.conductTasks.length - 5);
    const tasksCount = bucket.conductTasks.length;

    const popoverWidth = 360;
    const popoverApproxHeight = 240;
    const margin = 16;
    const gap = 8;

    const cellCenterX = rect.left + rect.width / 2;
    let x = cellCenterX - popoverWidth / 2;
    if (x < margin) {
      x = margin;
    } else if (x + popoverWidth > window.innerWidth - margin) {
      x = window.innerWidth - popoverWidth - margin;
    }

    const caretOffset = Math.max(16, Math.min(popoverWidth - 16, cellCenterX - x));

    let y = 0;
    let placement: 'top' | 'bottom' = 'top';
    if (rect.top - popoverApproxHeight - gap < margin) {
      placement = 'bottom';
      y = rect.bottom + gap;
    } else {
      placement = 'top';
      y = rect.top - gap;
    }

    this.hoveredConductTooltipData = {
      projectName,
      slotLoggedTime,
      tasksCount,
      tasks,
      moreCount,
      usageComparison,
      x,
      y,
      caretOffset,
      placement
    };
  }

  onConductPillMouseLeave(): void {
    this.hoveredConductTooltipData = null;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.hoveredConductTooltipData) {
      this.hoveredConductTooltipData = null;
    }
  }

  /**
   * Format total seconds into standard theme format: "0h 0m", "1h 30m"
   */
  formatHoursMinutes(totalSeconds: number): string {
    if (!totalSeconds || isNaN(totalSeconds) || totalSeconds <= 0) {
      return '0h 0m';
    }
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }

  secondsToTimeString(totalSeconds: number): string {
    if (!totalSeconds || isNaN(totalSeconds) || totalSeconds <= 0) {
      return '00:00:00';
    }
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  // ── Conduct Drawer Integration ───────────────────────────────────────────────

  openConductDrawer(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.hoveredConductTooltipData = null;

    if (!this.conductTimelogs || this.conductTimelogs.length === 0) {
      return;
    }

    const group = this.buildConductGroupForCurrentContext();
    this.selectedConductGroup = group;
    this.conductDrawerGroups = [group];
    this.selectedConductGroupIndex = 0;
    this.isConductDrawerOpen = true;

    this.sharedService.componentDrawerSubject.next({
      componentName: 'conduct-timelogs',
      componentAction: 'open',
      componentWidth: 680,
      componentData: {
        group: this.selectedConductGroup,
        groups: this.conductDrawerGroups,
        currentIndex: this.selectedConductGroupIndex,
        selectedDate: this.selectedDate || this.serverDate,
        startDate: this.selectedDate || this.serverDate,
        endDate: this.selectedDate || this.serverDate,
        users: this.userDetails ? [this.userDetails] : (this.employees || []),
        userTimelineSessions: this.getCurrentDayRawSessions()
      }
    });
  }

  closeConductDrawer(): void {
    this.isConductDrawerOpen = false;
    this.selectedConductGroup = null;
    this.sharedService.componentDrawerSubject.next({
      componentName: 'conduct-timelogs',
      componentAction: 'close',
      componentData: null,
      componentWidth: 0
    });
  }

  onConductDrawerNavigate(index: number): void {
    if (index >= 0 && index < this.conductDrawerGroups.length) {
      this.selectedConductGroupIndex = index;
      this.selectedConductGroup = this.conductDrawerGroups[index];
    }
  }

  getCurrentDayRawSessions(): any[] {
    if (!this.logsByDate || !this.selectedDate) return [];
    const ymd = this.datePipe.transform(this.selectedDate, "yyyy-MM-dd") || '';
    const dayData = this.logsByDate[ymd] ?? (Array.isArray(this.logsByDate) ? this.logsByDate[0] : this.logsByDate);
    const sessions = Array.isArray(dayData?.sessions)
      ? dayData.sessions
      : (Array.isArray(this.logsByDate?.sessions) ? this.logsByDate.sessions : []);
    return sessions;
  }

  private buildConductGroupForCurrentContext(): ConductTimelogGroup {
    const empName = this.selectedEmployeeName || (this.userDetails?.name || 'Employee');
    const empEmail = this.userDetails?.email || this.userDetails?.user_email || '';
    const superseeUserId = this.userId || this.userDetails?.id;
    const conductUserId = this.conductTimelogs[0]?.conduct_user_id || '';

    let totalDurationSeconds = 0;
    (this.conductTimelogs || []).forEach((log: any) => {
      const dur = parseInt(String(log.duration_time_second), 10) || 0;
      totalDurationSeconds += dur;
    });

    const hours = Math.floor(totalDurationSeconds / 3600);
    const minutes = Math.floor((totalDurationSeconds % 3600) / 60);
    const seconds = totalDurationSeconds % 60;
    const totalWorkedHours = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    return {
      user_key: String(superseeUserId || 'default'),
      supersee_user_id: superseeUserId,
      conduct_user_id: conductUserId,
      employee_name: empName,
      employee_email: empEmail,
      total_duration_seconds: totalDurationSeconds,
      total_worked_hours: totalWorkedHours,
      logs: [...(this.conductTimelogs || [])]
    };
  }

}
